/**
 * Покупатели: who comes back, how much they have spent, what they bought.
 * The same customers the registers attach to checks (created there offline
 * or here); a phone is unique within the shop.
 */
import type { CustomerDetailDto, CustomerDto, Page, SalesPageDto } from '@pos/api-client';
import { ApiError } from '@pos/api-client';
import { formatAmount } from '@pos/business-logic';
import { BottomSheet, Button, EmptyState, ErrorState, Field, Input, ListGroup, ListRow, Money, SearchField, Skeleton, Textarea, toast } from '@pos/ui';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Pencil, Phone, Plus, UserRound } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, useAuth } from '../../app/auth';
import { HeaderButton, Screen } from '../../app/Screen';
import { when } from '../../lib/format';

const PAGE = 50;
const chevron = <ChevronRight className="size-5 text-ink-4" />;

function useCustomers(q: string) {
  const biz = useAuth((s) => s.businessId);
  return useInfiniteQuery({
    queryKey: ['customers', biz, q],
    queryFn: ({ pageParam }) => api.get<Page<CustomerDto>>('/customers', { q: q || undefined, page: pageParam, pageSize: PAGE }),
    initialPageParam: 1,
    getNextPageParam: (last, all) => (all.reduce((n, p) => n + p.items.length, 0) < last.total ? all.length + 1 : undefined),
    placeholderData: keepPreviousData,
  });
}

function CustomerSheet({ customer, open, onOpenChange }: { customer: CustomerDto | null; open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', phone: '', email: '', note: '' });
  const [lastOpen, setLastOpen] = useState(false);
  // Fill the form each time the sheet opens, from the customer or blank.
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) setForm({ name: customer?.name ?? '', phone: customer?.phone ?? '', email: customer?.email ?? '', note: customer?.note ?? '' });
  }
  const save = useMutation({
    mutationFn: () => {
      const body = { name: form.name.trim(), phone: form.phone.trim() || null, email: form.email.trim() || null, note: form.note.trim() || null };
      return customer ? api.patch<CustomerDto>(`/customers/${customer.id}`, body) : api.post<CustomerDto>('/customers', { id: crypto.randomUUID(), ...body });
    },
    onSuccess: async (c) => {
      await Promise.all([qc.invalidateQueries({ queryKey: ['customers'] }), qc.invalidateQueries({ queryKey: ['customer'] })]);
      toast.success(customer ? 'Сохранено' : 'Клиент добавлен', { description: 'Кассы увидят его при синхронизации' });
      onOpenChange(false);
      if (!customer) navigate(`/customers/${c.id}`);
    },
    onError: (e) =>
      toast.error('Не сохранено', {
        description: e instanceof ApiError ? (e.code === 'PHONE_TAKEN' || e.status === 409 ? 'Этот телефон уже у другого клиента' : (Object.values(e.fields)[0] ?? e.message)) : undefined,
      }),
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const emailBad = form.email.trim() !== '' && !/.+@.+\..+/.test(form.email.trim());
  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={customer ? 'Клиент' : 'Новый клиент'}
      footer={
        <Button size="xl" variant="primary" className="w-full" disabled={!form.name.trim() || emailBad} loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label="Имя" required>
          {(a) => <Input {...a} size="lg" value={form.name} onChange={set('name')} autoComplete="off" />}
        </Field>
        <Field label="Телефон" hint="По нему кассир находит клиента">
          {(a) => <Input {...a} size="lg" inputMode="tel" value={form.phone} onChange={set('phone')} placeholder="+998" />}
        </Field>
        <Field label="Email" error={emailBad ? 'Проверьте адрес' : undefined}>
          {(a) => <Input {...a} size="lg" type="email" value={form.email} onChange={set('email')} />}
        </Field>
        <Field label="Заметка">
          {(a) => <Textarea {...a} rows={2} value={form.note} onChange={set('note')} />}
        </Field>
      </div>
    </BottomSheet>
  );
}

export function CustomersPage() {
  const [q, setQ] = useState('');
  const query = useDeferredValue(q.trim());
  const list = useCustomers(query);
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;
  return (
    <Screen
      title="Клиенты"
      back="/more"
      actions={
        <HeaderButton label="Добавить клиента" onClick={() => setAdding(true)}>
          <Plus />
        </HeaderButton>
      }
    >
      <div className="space-y-3">
        <SearchField size="md" value={q} onChange={(e) => setQ(e.target.value)} onClear={() => setQ('')} placeholder="Имя или телефон" aria-label="Поиск клиента" />
        {list.isError ? (
          <ErrorState description="Не удалось загрузить клиентов." onRetry={() => void list.refetch()} />
        ) : list.isPending ? (
          <Skeleton className="h-64 rounded-lg" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<UserRound />}
            title={query ? 'Никого не нашли' : 'Клиентов пока нет'}
            description={query ? 'Проверьте имя или номер.' : 'Кассир добавляет клиента к чеку на кассе, или добавьте здесь.'}
          />
        ) : (
          <>
            <p className="px-1 text-sm text-ink-3 tnum">Всего: {total}</p>
            <ListGroup>
              {rows.map((c) => (
                <ListRow key={c.id} title={c.name} subtitle={c.phone ?? c.email ?? 'Без телефона'} trailing={chevron} onClick={() => navigate(`/customers/${c.id}`)} />
              ))}
            </ListGroup>
            {list.hasNextPage && (
              <Button size="lg" className="w-full" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                Показать ещё
              </Button>
            )}
          </>
        )}
      </div>
      <CustomerSheet customer={null} open={adding} onOpenChange={setAdding} />
    </Screen>
  );
}

export function CustomerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const c = useQuery({ queryKey: ['customer', id], queryFn: () => api.get<CustomerDetailDto>(`/customers/${id}`), enabled: !!id });
  // A year of purchases: the card is about the relationship, not today.
  const since = new Date(Date.now() - 365 * 86_400_000).toISOString().slice(0, 10);
  const sales = useQuery({
    queryKey: ['customer-sales', id],
    queryFn: () => api.get<SalesPageDto>('/sales', { customerId: id, preset: 'custom', from: since, limit: 30 }),
    enabled: !!id,
  });
  return (
    <Screen
      title={c.data?.name ?? 'Клиент'}
      back="/customers"
      actions={
        c.data ? (
          <HeaderButton label="Изменить" onClick={() => setEditing(true)}>
            <Pencil />
          </HeaderButton>
        ) : undefined
      }
    >
      {c.isError ? (
        <ErrorState description="Клиент не найден или нет связи." onRetry={() => void c.refetch()} />
      ) : !c.data ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-line bg-surface p-4">
              <p className="text-sm text-ink-3">Потратил</p>
              <Money value={c.data.spent} className="mt-1 block text-2xl font-bold" />
            </div>
            <div className="rounded-2xl border border-line bg-surface p-4">
              <p className="text-sm text-ink-3">Покупок</p>
              <p className="mt-1 text-2xl font-bold tnum">{c.data.purchases}</p>
              <p className="text-sm text-ink-3">{c.data.lastPurchaseAt ? `последняя ${when(c.data.lastPurchaseAt)}` : 'ещё не покупал'}</p>
            </div>
          </div>
          <ListGroup>
            {c.data.phone && (
              <ListRow
                leading={<Phone className="size-5 text-primary" />}
                title={c.data.phone}
                subtitle="Позвонить"
                onClick={() => {
                  window.location.href = `tel:${c.data.phone}`;
                }}
              />
            )}
            {c.data.email && <ListRow title={c.data.email} subtitle="Email" />}
            {c.data.note && <ListRow title={c.data.note} subtitle="Заметка" />}
            <ListRow title={when(c.data.createdAt)} subtitle="Клиент с" />
          </ListGroup>
          <ListGroup title="Покупки за год">
            {sales.isError ? (
              <ListRow title={<span className="text-ink-3">Не удалось загрузить покупки</span>} />
            ) : !sales.data ? (
              <ListRow title={<span className="text-ink-3">Загрузка…</span>} />
            ) : sales.data.items.length === 0 ? (
              <ListRow title={<span className="text-ink-3">Покупок за год нет</span>} />
            ) : (
              sales.data.items.map((s) => (
                <ListRow
                  key={s.id}
                  title={`Чек ${s.number}`}
                  subtitle={`${when(s.completedAt)} · ${s.branchName}`}
                  trailing={<span className="font-medium tnum">{formatAmount(s.total)}</span>}
                  onClick={() => navigate(`/sales/${s.id}`)}
                />
              ))
            )}
          </ListGroup>
        </div>
      )}
      <CustomerSheet customer={c.data ?? null} open={editing} onOpenChange={setEditing} />
    </Screen>
  );
}
